import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { request, BaseResponse } from "../../../lib/api";
import { Toast, Card, Button, Input, Tag, Space, Divider, SpinLoading, Switch, Slider } from "antd-mobile";

// 自定义Typography组件
const Title = ({ level, children, style }: { level?: number; children: React.ReactNode; style?: React.CSSProperties }) => {
  const fontSize = level === 2 ? '24px' : level === 5 ? '4px' : '60px';
  return <div style={{ fontSize, fontWeight: 'bold', margin: '4px 0', ...style }}>{children}</div>;
};

const Text = ({ strong, type, copyable, children, style }: { 
  strong?: boolean; 
  type?: 'secondary' | 'danger'; 
  copyable?: boolean;
  children: React.ReactNode; 
  style?: React.CSSProperties 
}) => {
  const textStyle = {
    fontWeight: strong ? 'bold' : 'normal',
    color: type === 'secondary' ? '#999' : type === 'danger' ? '#ff4d4f' : '#000',
    cursor: copyable ? 'pointer' : 'default',
    ...style
  };
  
  const handleCopy = () => {
    if (copyable && typeof children === 'string') {
      navigator.clipboard.writeText(children);
      Toast.show('已复制到剪贴板');
    }
  };
  
  return <span style={textStyle} onClick={copyable ? handleCopy : undefined}>{children}</span>;
};

const Paragraph = ({ children }: { children: React.ReactNode }) => {
  return <p style={{ margin: '8px 0', lineHeight: '1.6' }}>{children}</p>;
};

// 手风琴组件
interface AccordionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  icon?: string;
  badge?: string | number;
}

const Accordion: React.FC<AccordionProps> = ({ 
  title, 
  children, 
  defaultExpanded = true, 
  icon = "📋",
  badge 
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div style={{ 
      marginBottom: 12, 
      border: "1px solid #e1e5e9", 
      borderRadius: 8, 
      overflow: "hidden",
      background: "#fff"
    }}>
      {/* 标题栏 */}
      <div 
        style={{ 
          padding: "2px 16px", 
          background: isExpanded ? "#f8f9fa" : "#fff",
          borderBottom: isExpanded ? "1px solid #e1e5e9" : "none",
          cursor: "pointer", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          transition: "all 0.2s ease"
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
          {badge && (
            <span style={{ 
              background: "#1890ff", 
              color: "#fff", 
              padding: "2px 8px", 
              borderRadius: 12, 
              fontSize: 12,
              fontWeight: 500
            }}>
              {badge}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ fontSize: 12, color: "#666" }}
        >
          ▼
        </motion.div>
      </div>
      
      {/* 内容区域 */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: 16 }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// 数据类型定义
interface SessionDetail {
  session_id: string;
  user_id: string;
  scenario_id: string;
  scenario_name: string;
  scenario_description: string;
  status: string | number;
  created_at: string;
  updated_at: string;
  message_count: number;
  duration: number;
  user_info: {
    dept_user_name: string;
    avatar_color: string;
  };
}

interface StepProgress {
  current_step: {
    step_name: string;
    step_id: string;
    round_count: number;
    max_rounds: number;
    status: string | number;
  };
  all_steps: Array<{
    step_name: string;
    step_id: string;
    status: string | number;
    round_count: number;
    max_rounds: number;
    current_rounds: number;
  }>;
}

interface SpecialInfoLog {
  id: string;
  info_type: string;
  title: string;
  description: string;
  target_data: any;
  created_at: string;
  updated_at: string;
}

interface MessageWithLogs {
  id: string;
  content: string;
  audio_file_path?: string;
  audio_duration?: number;
  timestamp: string;
  message_type: string;
  scores?: any;
  speaker: string;
  special_info_logs: SpecialInfoLog[];
}

interface DebugInfo {
  session_detail: SessionDetail;
  step_progress: StepProgress;
  messages_with_logs: MessageWithLogs[];
  session_special_info_logs: SpecialInfoLog[];  // 新增session级别的特殊信息
  summary: {
    total_messages: number;
    total_special_logs: number;
    total_session_logs: number;  // 新增session级别日志数量
    current_step: string;
    session_status: string;
    scenario_name: string;
  };
}

const AiTrainingDebug: React.FC = () => {
  const [sessionId, setSessionId] = useState<string>("");
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<MessageWithLogs | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [showSettings, setShowSettings] = useState(false);
  const [activeLogTab, setActiveLogTab] = useState<string>("all");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // 手风琴展开状态
  const [accordionStates, setAccordionStates] = useState({
    sessionDetail: true,
    stepProgress: true,
    sessionSpecialInfo: true,
    messageList: true
  });

  // 日志类型配置
  const logTypeConfig: { [key: string]: { label: string; color: string; description: string } } = {
    "message_analysis_suggestion": {
      label: "回复建议",
      color: "blue",
      description: "AI助答提示和回复建议"
    },
    "stage_flow": {
      label: "对话流程控制信息",
      color: "green", 
      description: "对话阶段流程控制相关信息"
    },
    "deviation_analysis": {
      label: "偏离度分析",
      color: "red",
      description: "分析学员对话是否偏离陪练场景主题"
    },
    "student_response_scoring": {
      label: "学员响应评分",
      color: "orange",
      description: "对学员在陪练场景中的响应进行评分和指导"
    },
    // 保留原有的message_analysis作为兼容
    "message_analysis": {
      label: "对话分析",
      color: "purple",
      description: "通用对话分析（兼容旧数据）"
    },
    // 默认配置
    "default": {
      label: "未知类型",
      color: "default",
      description: "未知的日志类型"
    }
  };

  // 获取日志类型的显示名称
  const getLogTypeLabel = (infoType: string) => {
    return logTypeConfig[infoType as keyof typeof logTypeConfig]?.label || infoType;
  };

  // 获取日志类型的颜色
  const getLogTypeColor = (infoType: string) => {
    return logTypeConfig[infoType as keyof typeof logTypeConfig]?.color || "default";
  };

  // 按类型分组日志
  const groupLogsByType = (logs: SpecialInfoLog[]) => {
    const grouped: { [key: string]: SpecialInfoLog[] } = {};
    logs.forEach(log => {
      if (!grouped[log.info_type]) {
        grouped[log.info_type] = [];
      }
      grouped[log.info_type].push(log);
    });
    return grouped;
  };

  // URL参数解析 - 页面加载时检查URL参数
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get('id');
    if (idParam) {
      setSessionId(idParam);
    }
  }, []); // 只在组件挂载时执行一次

  // 当sessionId从URL参数设置后，自动获取调试信息
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get('id');
    if (idParam && sessionId === idParam && !debugInfo) {
      fetchDebugInfo();
    }
  }, [sessionId]); // 当sessionId变化时执行

  // 自动刷新逻辑
  useEffect(() => {
    if (autoRefresh && sessionId.trim() && debugInfo) {
      intervalRef.current = setInterval(() => {
        fetchDebugInfo();
      }, refreshInterval * 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, sessionId, debugInfo]);

  // 获取调试信息
  const fetchDebugInfo = async () => {
    if (!sessionId.trim()) {
      Toast.show("请输入Session ID");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response: BaseResponse<DebugInfo> = await request(`/conversations/sessions/${sessionId}/debug`, {
        method: "GET",
      });
console.log('response===>',response);

      if (response.success && response.data) {
        setDebugInfo(response.data);
        Toast.show("调试信息获取成功");
      } else {
        setError(response.errorMessage || "获取调试信息失败");
        Toast.show(response.errorMessage || "获取调试信息失败");
      }
    } catch (err: any) {
      const errorMsg = err.message || "网络请求失败";
      setError(errorMsg);
      Toast.show(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // 格式化时间
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("zh-CN");
  };

  // 格式化持续时间
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}小时${minutes}分钟${secs}秒`;
    } else if (minutes > 0) {
      return `${minutes}分钟${secs}秒`;
    } else {
      return `${secs}秒`;
    }
  };

  // 获取状态颜色
  const getStatusColor = (status: string | number) => {
    console.log('status===>',status);
    
    // 将status转换为字符串并转为小写
    const statusStr = String(status).toLowerCase();
    
    switch (statusStr) {
      case "completed":
      case "passed":
      case "2": // 已完成状态
        return "green";
      case "failed":
      case "error":
      case "4": // 超时结束状态
        return "red";
      case "in_progress":
      case "active":
      case "0": // 对话中状态
      case "1": // 评分中状态
        return "blue";
      case "pending":
      case "3": // 评分失败状态
        return "orange";
      default:
        return "default";
    }
  };

  // 获取消息类型颜色
  const getMessageTypeColor = (messageType: string) => {
    switch (messageType.toLowerCase()) {
      case "user":
        return "blue";
      case "ai":
      case "assistant":
        return "green";
      case "system":
        return "purple";
      default:
        return "default";
    }
  };

  // 渲染会话详情
  const renderSessionDetail = () => {
    if (!debugInfo?.session_detail) return null;

    const { session_detail } = debugInfo;

    return (
      <Accordion 
        title="会话详情" 
        icon="👤" 
        badge={session_detail.message_count}
        defaultExpanded={accordionStates.sessionDetail}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, fontSize: 12 }}>
          <div><strong>ID:</strong> <span style={{ fontFamily: "monospace" }}>{session_detail.session_id}</span></div>
          <div><strong>用户:</strong> {session_detail.user_info?.dept_user_name || "未知"}</div>
          <div><strong>场景:</strong> {session_detail.scenario_name}</div>
          <div><strong>状态:</strong> <Tag color={getStatusColor(session_detail.status)} style={{ fontSize: 10 }}>{session_detail.status}</Tag></div>
          <div><strong>消息:</strong> {session_detail.message_count}</div>
          <div><strong>时长:</strong> {formatDuration(session_detail.duration || 0)}</div>
          <div><strong>创建:</strong> {formatTime(session_detail.created_at)}</div>
          <div><strong>更新:</strong> {formatTime(session_detail.updated_at)}</div>
        </div>
        {session_detail.scenario_description && (
          <div style={{ marginTop: 12, padding: 8, background: "#f8f9fa", borderRadius: 4, fontSize: 12 }}>
            <strong>描述:</strong> {session_detail.scenario_description}
          </div>
        )}
      </Accordion>
    );
  };

  // 渲染环节状态
  const renderStepProgress = () => {
    if (!debugInfo?.step_progress) return null;

    const { step_progress } = debugInfo;

    return (
      <Accordion 
        title="环节状态" 
        icon="🔄" 
        badge={step_progress.all_steps?.length || 0}
        defaultExpanded={accordionStates.stepProgress}
      >
        {step_progress.current_step && (
          <div style={{ marginBottom: 12, padding: 8, background: "#e3f2fd", borderRadius: 4, fontSize: 12 }}>
            <strong>当前:</strong> {step_progress.current_step.step_name} 
            <Tag color={getStatusColor(step_progress.current_step.status)} style={{ fontSize: 10, marginLeft: 4 }}>
              {step_progress.current_step.status}
            </Tag>
            <span style={{ marginLeft: 8 }}>
              {step_progress.current_step.round_count}/{step_progress.current_step.max_rounds}轮
            </span>
          </div>
        )}

        {step_progress.all_steps && step_progress.all_steps.length > 0 && (
          <div>
            <div style={{ fontWeight: "bold", marginBottom: 8, fontSize: 12 }}>所有环节:</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
              {step_progress.all_steps.map((step, index) => (
                <div key={step.step_id || index} style={{ 
                  border: "1px solid #ced4da", 
                  borderRadius: 4, 
                  padding: 8,
                  fontSize: 11,
                  background: "#fff"
                }}>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>{step.step_name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    <Tag color={getStatusColor(step.status)} style={{ fontSize: 9 }}>{step.status}</Tag>
                    <span>{step.round_count}/{step.max_rounds}</span>
                    <span style={{ color: "#6c757d" }}>已{step.current_rounds}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Accordion>
    );
  };

  // 渲染会话级别的特殊信息
  const renderSessionSpecialInfo = () => {
    if (!debugInfo?.session_special_info_logs || debugInfo.session_special_info_logs.length === 0) {
      return null;
    }

    const { session_special_info_logs } = debugInfo;

    return (
      <Accordion 
        title="会话级别特殊信息" 
        icon="🔍" 
        badge={session_special_info_logs.length}
        defaultExpanded={accordionStates.sessionSpecialInfo}
      >
        
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {session_special_info_logs.map((log, index) => {
            const config = logTypeConfig[log.info_type] || logTypeConfig.default;
            
            return (
              <div key={log.id || index} style={{ 
                border: "1px solid #ced4da", 
                borderRadius: 4, 
                padding: 6,
                background: "#fff",
                borderLeft: `4px solid ${config.color}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <Tag color={config.color} style={{ fontSize: 10 }}>
                    {config.label}
                  </Tag>
                  <span style={{ fontWeight: "bold", fontSize: 11 }}>{log.title}</span>
                  <span style={{ color: "#6c757d", fontSize: 10, marginLeft: "auto" }}>
                    {formatTime(log.created_at)}
                  </span>
                </div>
                
                {log.description && (
                  <div style={{ 
                    marginBottom: 4, 
                    fontSize: 11, 
                    color: "#495057",
                    lineHeight: 1.4
                  }}>
                    {log.description}
                  </div>
                )}
                
                {log.target_data && (
                  <div style={{ 
                    background: "#f8f9fa", 
                    border: "1px solid #e9ecef", 
                    borderRadius: 2, 
                    padding: 4,
                    fontSize: 10,
                    fontFamily: "monospace",
                    maxHeight: 100,
                    overflow: "auto"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                      <span style={{ fontWeight: "bold" }}>数据:</span>
                      <button
                        onClick={() => copyJsonData(log.target_data)}
                        style={{
                          background: "none",
                          border: "1px solid #ced4da",
                          borderRadius: 2,
                          padding: "2px 6px",
                          fontSize: 9,
                          cursor: "pointer",
                          color: "#007bff"
                        }}
                      >
                        复制JSON
                      </button>
                    </div>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {JSON.stringify(log.target_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Accordion>
    );
  };

  // 渲染对话列表
  const renderMessages = () => {
    if (!debugInfo?.messages_with_logs) return null;

    const { messages_with_logs } = debugInfo;

    return (
      <Accordion 
        title="对话列表" 
        icon="💬" 
        badge={messages_with_logs.length}
        defaultExpanded={accordionStates.messageList}
      >
        <div style={{ 
          flex: 1, 
          overflowY: "auto",
          minHeight: 0 // 重要：确保可以滚动
        }}>
          {messages_with_logs.map((message, index) => (
            <div
              key={message.id}
              onClick={() => {
                setSelectedMessage(message);
                setActiveLogTab("all"); // 重置Tab状态
              }}
              style={{
                cursor: "pointer",
                border: selectedMessage?.id === message.id ? "2px solid #007bff" : "1px solid #ced4da",
                borderRadius: 2,
                padding: 6,
                marginBottom: 4,
                background: selectedMessage?.id === message.id ? "#e3f2fd" : "#fff",
                fontSize: 11,
                flexShrink: 0 // 防止消息项被压缩
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                <Tag color={getMessageTypeColor(message.speaker)} style={{ fontSize: 9 }}>{message.speaker}</Tag>
                <Tag color={getMessageTypeColor(message.message_type)} style={{ fontSize: 9 }}>{message.message_type}</Tag>
                {message.special_info_logs.length > 0 && (
                  <Tag color="orange" style={{ fontSize: 9 }}>
                    {message.special_info_logs.length}日志
                  </Tag>
                )}
                <span style={{ fontSize: 9, color: "#6c757d", marginLeft: "auto" }}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </span>
              </div>
              <div style={{ marginBottom: 4, lineHeight: 1.3 }}>
                {message.content ? (
                  message.content.length > 100 ? 
                    message.content.substring(0, 100) + "..." : 
                    message.content
                ) : "无文本内容"}
              </div>
              {message.audio_duration && (
                <div style={{ fontSize: 9, color: "#6c757d" }}>
                  音频: {message.audio_duration.toFixed(1)}s
                </div>
              )}
            </div>
          ))}
        </div>
      </Accordion>
    );
  };

  // 复制JSON数据到剪贴板
  const copyJsonData = (data: any) => {
    const jsonString = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
      Toast.show('JSON数据已复制到剪贴板');
    }).catch(() => {
      Toast.show('复制失败');
    });
  };

  // 渲染单个日志项
  const renderLogItem = (log: SpecialInfoLog) => (
    <div
      key={log.id}
      style={{ 
        border: "1px solid #ced4da", 
        borderRadius: 4, 
        padding: 8, 
        marginBottom: 8,
        background: "#fff",
        fontSize: 12,
        flexShrink: 0
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <Tag color={getLogTypeColor(log.info_type)} style={{ fontSize: 10 }}>
          {getLogTypeLabel(log.info_type)}
        </Tag>
        <strong style={{ fontSize: 13 }}>{log.title}</strong>
        <span style={{ fontSize: 10, color: "#6c757d", marginLeft: "auto" }}>
          {new Date(log.created_at).toLocaleTimeString()}
        </span>
      </div>
      <div style={{ marginBottom: 8, lineHeight: 1.4 }}>
        <strong>描述:</strong> {log.description}
      </div>
      {log.target_data && (
        <div>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between", 
            marginBottom: 4 
          }}>
            <strong>数据:</strong>
            <button
              onClick={() => copyJsonData(log.target_data)}
              style={{
                padding: "2px 6px",
                fontSize: 10,
                border: "1px solid #007bff",
                borderRadius: 3,
                background: "#fff",
                color: "#007bff",
                cursor: "pointer"
              }}
            >
              📋 复制JSON
            </button>
          </div>
          <pre style={{ 
            background: "#f8f9fa", 
            padding: 8, 
            borderRadius: 4, 
            fontSize: 11,
            overflow: "auto",
            maxHeight: 300,
            margin: 0,
            border: "1px solid #e9ecef",
            lineHeight: 1.3,
            fontFamily: "Monaco, Menlo, 'Ubuntu Mono', monospace"
          }}>
            {JSON.stringify(log.target_data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );

  // 渲染特殊信息日志
  const renderSpecialInfoLogs = () => {
    if (!selectedMessage || !selectedMessage.special_info_logs.length) {
      return (
        <div style={{ 
          background: "#f8f9fa", 
          border: "1px solid #dee2e6", 
          borderRadius: 4, 
          padding: 8,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0
        }}>
          <div style={{ fontWeight: "bold", marginBottom: 8, fontSize: 14, flexShrink: 0 }}>特殊信息日志</div>
          <div style={{ color: "#6c757d", fontSize: 12 }}>请选择一条消息查看相关日志</div>
        </div>
      );
    }

    const groupedLogs = groupLogsByType(selectedMessage.special_info_logs);
    const logTypes = Object.keys(groupedLogs);
    
    // 如果当前选中的tab不存在，重置为"all"
    if (activeLogTab !== "all" && !logTypes.includes(activeLogTab)) {
      setActiveLogTab("all");
    }

    // 获取要显示的日志
    const logsToShow = activeLogTab === "all" 
      ? selectedMessage.special_info_logs 
      : groupedLogs[activeLogTab] || [];

    return (
      <div style={{ 
        background: "#f8f9fa", 
        border: "1px solid #dee2e6", 
        borderRadius: 4, 
        padding: 8,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0
      }}>
        {/* 标题 */}
        <div style={{ 
          fontWeight: "bold", 
          marginBottom: 8, 
          fontSize: 14, 
          flexShrink: 0
        }}>
          日志详情 ({selectedMessage.special_info_logs.length})
        </div>
        
        {/* 消息信息 */}
        <div style={{ 
          marginBottom: 8, 
          padding: 6, 
          background: "#e9ecef", 
          borderRadius: 4, 
          fontSize: 11,
          flexShrink: 0
        }}>
          消息: {selectedMessage.content?.substring(0, 50)}...
        </div>

        {/* Tab导航 */}
        <div style={{ 
          display: "flex", 
          gap: 6, 
          marginBottom: 12, 
          flexWrap: "wrap",
          flexShrink: 0,
          borderBottom: "1px solid #e9ecef",
          paddingBottom: 8
        }}>
          <button
            onClick={() => setActiveLogTab("all")}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              border: activeLogTab === "all" ? "2px solid #007bff" : "1px solid #ced4da",
              borderRadius: 6,
              background: activeLogTab === "all" ? "#e3f2fd" : "#fff",
              color: activeLogTab === "all" ? "#007bff" : "#495057",
              cursor: "pointer",
              fontWeight: activeLogTab === "all" ? "bold" : "normal",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              if (activeLogTab !== "all") {
                e.currentTarget.style.background = "#f8f9fa";
              }
            }}
            onMouseLeave={(e) => {
              if (activeLogTab !== "all") {
                e.currentTarget.style.background = "#fff";
              }
            }}
          >
            全部 ({selectedMessage.special_info_logs.length})
          </button>
          {logTypes.map(type => (
            <button
              key={type}
              onClick={() => setActiveLogTab(type)}
              style={{
                padding: "6px 12px",
                fontSize: 12,
                border: activeLogTab === type ? "2px solid #007bff" : "1px solid #ced4da",
                borderRadius: 6,
                background: activeLogTab === type ? "#e3f2fd" : "#fff",
                color: activeLogTab === type ? "#007bff" : "#495057",
                cursor: "pointer",
                fontWeight: activeLogTab === type ? "bold" : "normal",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if (activeLogTab !== type) {
                  e.currentTarget.style.background = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                if (activeLogTab !== type) {
                  e.currentTarget.style.background = "#fff";
                }
              }}
            >
              {getLogTypeLabel(type)} ({groupedLogs[type].length})
            </button>
          ))}
        </div>

        {/* 日志内容 */}
        <div style={{ 
          flex: 1, 
          overflowY: "auto",
          minHeight: 0
        }}>
          {logsToShow.length > 0 ? (
            logsToShow.map(log => renderLogItem(log))
          ) : (
            <div style={{ color: "#6c757d", fontSize: 12, textAlign: "center", padding: 20 }}>
              该类型暂无日志数据
            </div>
          )}
        </div>
      </div>
    );
  };

  // 渲染汇总信息
  const renderSummary = () => {
    if (!debugInfo?.summary) return null;

    const { summary } = debugInfo;

    return (
      <Card title="汇总信息" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
          <div style={{ textAlign: "center", padding: 16, background: "#f0f9ff", borderRadius: 8 }}>
            <div style={{ fontSize: 24, fontWeight: "bold", color: "#1890ff" }}>{summary.total_messages}</div>
            <div>总消息数</div>
          </div>
          <div style={{ textAlign: "center", padding: 16, background: "#fff7e6", borderRadius: 8 }}>
            <div style={{ fontSize: 24, fontWeight: "bold", color: "#fa8c16" }}>{summary.total_special_logs}</div>
            <div>特殊日志数</div>
          </div>
          <div style={{ textAlign: "center", padding: 16, background: "#f6ffed", borderRadius: 8 }}>
            <div style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>{summary.current_step}</div>
            <div>当前环节</div>
          </div>
          <div style={{ textAlign: "center", padding: 16, background: "#f9f0ff", borderRadius: 8 }}>
            <div style={{ fontSize: 16, fontWeight: "bold", color: "#722ed1" }}>{summary.session_status}</div>
            <div>会话状态</div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ 
      height: "100vh", 
      display: "flex", 
      flexDirection: "column",
      background: "#f8f9fa",
      overflow: "hidden"
    }}>
      {/* 主要内容容器 */}
      <div style={{
        height: "100vh",
        maxHeight: "100vh",
        overflow: "auto",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* 顶部信息区域 - 固定 */}
        <div style={{ 
          background: "#fff", 
          borderBottom: "1px solid #dee2e6", 
          padding: 12,
          position: "sticky",
          top: 0,
          zIndex: 100,
          flexShrink: 0
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>AI训练调试</h2>
          <Input
            placeholder="输入会话ID"
            value={sessionId}
            onChange={(value) => setSessionId(value)}
            style={{ width: 200 }}
          />
          <Button color="primary" onClick={fetchDebugInfo} loading={loading} size="small">
            获取调试信息
          </Button>
          
          {/* 设置区域 */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            <Button 
              size="small" 
              onClick={() => setShowSettings(!showSettings)}
            >
              ⚙️ 设置
            </Button>
          </div>
        </div>
        
        {/* 设置面板 */}
        {showSettings && (
          <div style={{ 
            background: "#f8f9fa", 
            border: "1px solid #dee2e6", 
            borderRadius: 4, 
            padding: 12,
            display: "flex",
            alignItems: "center",
            gap: 16,
            fontSize: 12
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
               <span>自动刷新:</span>
               <Switch 
                 checked={autoRefresh} 
                 onChange={setAutoRefresh} 
               />
             </div>
             <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
               <span>间隔(秒):</span>
               <Slider
                 min={1}
                 max={30}
                 value={refreshInterval}
                 onChange={(value) => setRefreshInterval(Array.isArray(value) ? value[0] : value)}
                 style={{ width: 100 }}
               />
               <span>{refreshInterval}s</span>
             </div>
          </div>
        )}

        {/* 会话详情和步骤进度 */}
        {debugInfo && (
          <div style={{ marginTop: 12 }}>
            {renderSessionDetail()}
            {renderStepProgress()}
            {renderSessionSpecialInfo()}
          </div>
        )}
      </div>

        {/* 主要内容区域 */}
        {debugInfo && (
          <div style={{ 
            flex: 1, 
            display: "flex", 
            gap: 8, 
            padding: 8,
            overflow: "hidden",
            minHeight: 0
          }}>
          {/* 左侧对话列表 */}
          <div style={{ 
            width: "50%",
            overflow: "auto",
            maxHeight: "100%"
          }}>
            {renderMessages()}
          </div>
          
          {/* 右侧特殊信息日志 */}
          <div style={{ 
            width: "50%",
            overflow: "auto",
            maxHeight: "100%"
          }}>
            {renderSpecialInfoLogs()}
          </div>
        </div>
      )}

      {/* 悬浮刷新按钮 */}
      <Button
        color="primary"
        onClick={fetchDebugInfo}
        loading={loading}
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          fontSize: 18,
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          borderRadius: "50%"
        }}
      >
        🔄
      </Button>
      </div>
    </div>
  );
};

export default AiTrainingDebug;